import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Briefcase, PlusCircle, Users, Edit, Trash2, Eye } from 'lucide-react';

export default function MyOpportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const response = await api.get('/opportunities/my');
        setOpportunities(response.data || []);
      } catch (error) {
        console.error('Error fetching opportunities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOpportunities();
  }, []);
  
  const deleteOpportunity = async (oppId) => {
    if (!window.confirm('Are you sure you want to delete this opportunity? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/opportunities/${oppId}`);
      setOpportunities(prev => prev.filter(opp => opp.opportunity_id !== oppId));
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      alert('Failed to delete opportunity');
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout title="My Opportunities">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D7377]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="My Opportunities">
      {/* Header Action */}
      <div className="flex justify-end mb-6">
        <Link
          to="/client/opportunities/new"
          data-testid="create-opportunity-btn"
          className="btn-client px-6 py-2.5 flex items-center gap-2"
        >
          <PlusCircle size={18} />
          Post New Opportunity
        </Link>
      </div>
      
      {opportunities.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <Briefcase className="text-neutral-300 mx-auto mb-4" size={48} />
          <p className="text-neutral-500 mb-4">You haven't posted any opportunities yet</p>
          <Link
            to="/client/opportunities/new"
            className="btn-client px-6 py-3 inline-flex items-center gap-2"
          >
            <PlusCircle size={18} />
            Post Your First Opportunity
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {opportunities.map((opp) => (
            <div
              key={opp.opportunity_id}
              data-testid={`opportunity-${opp.opportunity_id}`}
              className="bg-white border border-neutral-200 p-6 card-client"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Link
                      to={`/opportunity/${opp.opportunity_id}`}
                      className="font-semibold text-lg text-neutral-900 hover:text-[#0D7377] transition-colors"
                    >
                      {opp.title}
                    </Link>
                    <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${
                      opp.status === 'open' 
                        ? 'bg-green-100 text-green-700' 
                        : opp.status === 'filled'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-neutral-100 text-neutral-600'
                    }`}>
                      {opp.status}
                    </span>
                  </div>
                  
                  <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                    {opp.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {opp.skills_needed?.slice(0, 4).map((skill, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-xs text-neutral-400 mt-3">
                    Posted {new Date(opp.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="flex flex-col items-end gap-3">
                  <Link
                    to={`/opportunity/${opp.opportunity_id}`}
                    data-testid={`view-apps-${opp.opportunity_id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-[#E6F3F4] text-[#0D7377] font-medium text-sm hover:bg-[#BCE3E4] transition-colors"
                  >
                    <Users size={16} />
                    {opp.application_count || 0} Applications
                  </Link>
                  
                  <div className="flex gap-2">
                    <Link
                      to={`/opportunity/${opp.opportunity_id}`}
                      className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                      title="View"
                    >
                      <Eye size={18} />
                    </Link>
                    <button
                      data-testid={`delete-${opp.opportunity_id}`}
                      onClick={() => deleteOpportunity(opp.opportunity_id)}
                      className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
